import { Ref, ref } from 'vue';

import { buildAddress } from '@/helpers/address';
import { Address } from '@/interfaces/repositories/utility';
import { GoogleMapsLoader, installGoogleMaps } from '@/plugins/googleMaps';
import { AvailableHolidayState, states } from '@/utils/holidayList';
import { getBrowserLanguage } from '@/utils/languages';
import { statePostalCodeList } from '@/utils/statePostalCodes';

import { LocationPrediction, LocationService, LocationServiceQueryResult } from './location';

export class GoogleMapsLocationService implements LocationService {
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  public canUseLocation = ref(false);

  private addressToPlaceLookupCache: Map<string, string> = new Map();

  private googleMapsLoader: GoogleMapsLoader;

  public constructor() {
    this.googleMapsLoader = installGoogleMaps();

    this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.googleMapsLoader.importLibrary('places');

    this.canUseLocation.value = true;

    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  public tryFetchState(postalCode: string): AvailableHolidayState | undefined {
    const numericPostalCode = parseInt(postalCode.trim(), 10);
    if (Number.isNaN(numericPostalCode)) return undefined;

    const entries = Object.entries(statePostalCodeList);

    for (const entry of entries) {
      const [stateName, statePostalCodes] = entry;
      for (let i = 0; i < statePostalCodes.length; i++) {
        const currentPostalCode = statePostalCodes[i];
        if (currentPostalCode === numericPostalCode) {
          return mapStateNameToHolidayState(stateName);
        } else if (currentPostalCode > numericPostalCode) {
          // sorted in ascending order, seeing larger code means it's not code of Bundesland
          break;
        }
      }
    }

    return undefined;
  }

  public usePredictions(
    languageCode?: string,
  ): LocationServiceQueryResult<string, LocationPrediction[]> {
    const predictions = ref([]) as Ref<LocationPrediction[]>;
    const loading = ref(false);

    return {
      result: predictions,
      refetch: (searchTerm) => {
        loading.value = true;
        this.getPredictions(searchTerm, predictions, loading, languageCode);
      },
      loading,
    };
  }

  public usePlace(languageCode?: string): LocationServiceQueryResult<string, Address> {
    const address = ref(buildAddress());
    const loading = ref(false);

    return {
      result: address,
      refetch: (id) => {
        loading.value = true;
        this.getPlace(id, address, loading, languageCode);
      },
      loading,
    };
  }

  public useState(languageCode?: string): LocationServiceQueryResult<string, string> {
    const state = ref('');
    const loading = ref(false);

    return {
      result: state,
      refetch: (address) => {
        this.getState(address, state, loading, languageCode);
      },
      loading,
    };
  }

  private getPredictions(
    searchTerm: string,
    result: Ref<LocationPrediction[]>,
    loading: Ref<boolean>,
    languageCode?: string,
  ): void {
    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: searchTerm,
      sessionToken: this.sessionToken ?? undefined,
      language: languageCode ?? getBrowserLanguage(),
    })
      .then(({ suggestions }: { suggestions: google.maps.places.AutocompleteSuggestion[] }) => {
        result.value = suggestions.map((suggestion: google.maps.places.AutocompleteSuggestion) => ({
          placeId: suggestion.placePrediction?.placeId ?? '',
          description: suggestion.placePrediction?.text.text ?? '',
        }));
      })
      .finally(() => {
        loading.value = false;
      });
  }

  private getPlace(
    id: string,
    result: Ref<Address>,
    loading: Ref<boolean>,
    languageCode?: string,
  ): void {
    if (!id) return;

    const place = new google.maps.places.Place({
      id: id,
      requestedLanguage: languageCode ?? getBrowserLanguage(),
    });

    place
      .fetchFields({
        fields: ['addressComponents'],
      })
      .then(({ place }: { place: google.maps.places.Place }) => {
        const components = place.addressComponents ?? [];
        const postalCode = components.find((data) => data.types.includes('postal_code')) ?? null;
        const street = components.find((data) => data.types.includes('route')) ?? null;
        const streetNumber =
          components.find((data) => data.types.includes('street_number')) ?? null;
        const city = components.find((data) => data.types.includes('locality')) ?? null;
        const country = components.find((data) => data.types.includes('country')) ?? null;

        result.value = {
          ...result.value,
          postalCode: postalCode?.longText ?? '',
          street: (street ? `${street.longText} ${streetNumber?.longText ?? ''}` : '').trim(),
          city: city?.longText ?? '',
          country: country?.shortText ?? '',
        };
      })
      .finally(() => {
        loading.value = false;
      });
  }

  private getState(
    address: string,
    result: Ref<string>,
    loading: Ref<boolean>,
    languageCode?: string,
  ): void {
    const cached = this.addressToPlaceLookupCache.get(address);
    if (cached) {
      result.value = cached;
      return;
    }

    loading.value = true;
    google.maps.places.Place.searchByText({
      textQuery: address,
      fields: ['addressComponents'],
      language: languageCode ?? getBrowserLanguage(),
    })
      .then(({ places }: { places: google.maps.places.Place[] }) => {
        if (!places?.length) return;

        const place = places[0];
        const components = place.addressComponents ?? [];
        const state =
          components.find((data) => data.types.includes('administrative_area_level_1'))?.longText ??
          '';
        this.addressToPlaceLookupCache.set(address, state);
        result.value = state;
      })
      .finally(() => {
        loading.value = false;
      });
  }
}

function mapStateNameToHolidayState(stateName: string): AvailableHolidayState | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const stateEntry = Object.entries(states.DE).find(([_state, name]) => name === stateName);

  return stateEntry ? (stateEntry[0] as unknown as AvailableHolidayState) : undefined;
}
